import { useState, useEffect, useMemo } from 'react';
import BasicFormInput from './BasicFormInput';
import MultiAutocomplete from './MultiAutocomplete';
import {
  getAllAffiliations,
  getAllCounties,
  getAllHUCs,
  getAllOutreachTypes,
  getAllStudyAreas,
  postAffiliations,
  postStudyAreas,
  postOutreachTypes
} from '../../utilities/affiliations';

import styles from '../styles/AddAffiliationForm.module.css';
import { renderArrayStr } from '../../utilities/shared';
const initAffState = {
  counties: [],
  huc_10s: [],
  study_areas: [],
  outreach_types: []
};

export default function AddAffiliationForm() {
  const [affiliationName, setAffiliationName] = useState('');
  const [affiliationsList, setAffiliationsList] = useState([]);
  const [rawCountiesList, setRawCountiesList] = useState([]);
  const [rawHUCsList, setRawHUCsList] = useState([]);
  const [rawOutreachList, setRawOutreachList] = useState([]);
  const [rawStudyAreasList, setRawStudyAreasList] = useState([]);
  const [affiliationData, setAffiliationsData] = useState(initAffState);
  const [outreachTypesData, setOutreachTypesData] = useState('');
  const [studyAreasData, setStudyAreasData] = useState('');
  const [refreshData, setRefreshData] = useState(false);

  const countiesList = useMemo(() => {
    return rawCountiesList.map((name) => ({
      label: name,
      value: name
    }));
  }, [rawCountiesList]);
  const HUCsList = useMemo(() => {
    return rawHUCsList.map((name) => ({
      label: name,
      value: name
    }));
  }, [rawHUCsList]);

  const outreachTypesList = useMemo(() => {
    return rawOutreachList.map((name) => ({
      label: name,
      value: name
    }));
  }, [rawOutreachList]);

  const studyAreasList = useMemo(() => {
    return rawStudyAreasList.map((name) => ({
      label: name,
      value: name
    }));
  }, [rawStudyAreasList]);

  async function fetchAffiliations() {
    const res = await getAllAffiliations();
    if (res?.status === 200) {
      const { data } = res;
      setAffiliationsList(data);
    }
  }
  async function fetchCounties() {
    const res = await getAllCounties();
    if (res?.status === 200) {
      const { data } = res;
      setRawCountiesList(data);
    }
  }
  async function fetchHUCs() {
    const res = await getAllHUCs();
    if (res?.status === 200) {
      const { data } = res;
      setRawHUCsList(data);
    }
  }
  async function fetchOutreachTypes() {
    const res = await getAllOutreachTypes();
    if (res?.status === 200) {
      const { data } = res;
      setRawOutreachList(data);
    }
  }
  async function fetchStudyAreas() {
    const res = await getAllStudyAreas();
    if (res?.status === 200) {
      const { data } = res;
      setRawStudyAreasList(data);
    }
  }

  useEffect(() => {
    fetchAffiliations();
    fetchCounties();
    fetchHUCs();
    fetchOutreachTypes();
    fetchStudyAreas();
  }, [refreshData]);

  async function submitAffiliationsHandler(e) {
    e.preventDefault();

    if (affiliationName === '') {
      alert('Please Fill in Affiliation Name');
      return;
    }
    const entries = Object.keys(initAffState).map((key) => {
      const values = affiliationData[key]
        .filter((option) => option.value)
        .map((option) => option.value);
      return [key, values];
    });
    const refinedData = Object.fromEntries(entries);
    const postData = {
      name: affiliationName,
      ...refinedData
    };
    const res = await postAffiliations(postData);
    if (res?.status === 200) {
      alert('Successfully Added Affiliation');
      setAffiliationsData(initAffState);
    }
  }
  async function submitOutreachTypesHandler(e) {
    e.preventDefault();

    const res = await postOutreachTypes(outreachTypesData);
    if (res?.status === 200) {
      alert('New outreach type added!');
      setRefreshData((curr) => !curr);
      setOutreachTypesData('');
    }
  }
  async function submitStudyAreasHandler(e) {
    e.preventDefault();

    const res = await postStudyAreas(studyAreasData);
    if (res?.status === 200) {
      alert('New study areas added!');
      setRefreshData((curr) => !curr);
      setStudyAreasData('');
    }
  }

  function onAutocompleteChange(key, val) {
    if (
      affiliationData.hasOwnProperty(key) &&
      affiliationData[key].some((obj) => obj.value === val.value)
    ) {
      return;
    }

    setAffiliationsData((prev) => ({
      ...prev,
      [key]: [...prev[key], val]
    }));
  }

  const updateReference = {
    affilName: setAffiliationName,
    outreach: setOutreachTypesData,
    studyArea: setStudyAreasData,
    reset: {
      counties: () =>
        setAffiliationsData((prev) => ({ ...prev, counties: [] })),
      huc_10s: () => setAffiliationsData((prev) => ({ ...prev, huc_10s: [] })),
      outreachTypes: () =>
        setAffiliationsData((prev) => ({ ...prev, outreach_types: [] })),
      studyAreas: () =>
        setAffiliationsData((prev) => ({ ...prev, study_areas: [] }))
    }
  };

  function handleInputChange(e) {
    const { name, value } = e.target;
    updateReference[name](value);
  }

  function handleReset(e) {
    const { name, value } = e.target;
    updateReference[name][value]();
  }

  return (
    <div className={styles.container}>
      <div className={styles.affBox}>
        <div className={styles.affInfoBox}>
          {affiliationsList.includes(affiliationName) && (
            <div className={`${styles.reverseRowContainer} ${styles.error}`}>
              *Affiliation name already taken
            </div>
          )}
          <BasicFormInput
            placeholder='Name...'
            label='New Affiliation Name:'
            updateKey='affilName'
            value={affiliationName}
            className={styles.nameInput}
            onChange={handleInputChange}
          />
          <div className={`${styles.rowContainerStart} ${styles.affInfoRow}`}>
            <h4 className={styles.label}>Counties:</h4>
            <div
              className={`${styles.rowContainerStart} ${styles.affInfoInputBox}`}
            >
              {renderArrayStr(
                affiliationData.counties.map((option) => option.label),
                'row'
              )}
            </div>
            <button
              onClick={handleReset}
              name='reset'
              value='counties'
              className={`${styles.bttn} ${styles.reset}`}
            >
              x
            </button>
          </div>

          <div className={`${styles.rowContainerStart} ${styles.affInfoRow}`}>
            <h4 className={styles.label}>HUC10s:</h4>
            <div
              className={`${styles.rowContainerStart} ${styles.affInfoInputBox}`}
            >
              {renderArrayStr(
                affiliationData.huc_10s.map((option) => option.label),
                'row'
              )}
            </div>
            <button
              onClick={handleReset}
              name='reset'
              value='huc_10s'
              className={`${styles.bttn} ${styles.reset}`}
            >
              x
            </button>
          </div>
          <div className={`${styles.rowContainerStart} ${styles.affInfoRow}`}>
            <h4 className={styles.label}>Study Areas:</h4>
            <div
              className={`${styles.rowContainerStart} ${styles.affInfoInputBox}`}
            >
              {renderArrayStr(
                affiliationData.study_areas.map((option) => option.label),
                'row'
              )}
            </div>
            <button
              onClick={handleReset}
              name='reset'
              value='studyAreas'
              className={`${styles.bttn} ${styles.reset}`}
            >
              x
            </button>
          </div>
          <div className={`${styles.rowContainerStart} ${styles.affInfoRow}`}>
            <h4 className={styles.label}>Outreach Types:</h4>
            <div
              className={`${styles.rowContainerStart} ${styles.affInfoInputBox}`}
            >
              {renderArrayStr(
                affiliationData.outreach_types.map((option) => option.label),
                'row'
              )}
            </div>
            <button
              onClick={handleReset}
              name='reset'
              value='outreachTypes'
              className={`${styles.bttn} ${styles.reset}`}
            >
              x
            </button>
          </div>
        </div>

        <form onSubmit={submitAffiliationsHandler}>
          <div className={styles.rowContainer}>
            <MultiAutocomplete
              placeholder='search...'
              className={styles.selectCounty}
              inputLabel='Select counties'
              optionsData={countiesList}
              onValueChange={(val) => onAutocompleteChange('counties', val)}
            />
            <MultiAutocomplete
              placeholder='search...'
              className={styles.selectHUC}
              inputLabel='Select HUC 10s'
              optionsData={HUCsList}
              onValueChange={(val) => onAutocompleteChange('huc_10s', val)}
            />
            <MultiAutocomplete
              placeholder='search...'
              className={styles.selectStudyArea}
              inputLabel='Select Study Areas'
              optionsData={studyAreasList}
              onValueChange={(val) => onAutocompleteChange('study_areas', val)}
            />
            <MultiAutocomplete
              placeholder='search...'
              className={styles.selectOutreach}
              inputLabel='Select Outreach Type'
              optionsData={outreachTypesList}
              onValueChange={(val) =>
                onAutocompleteChange('outreach_types', val)
              }
            />
          </div>
          <div className={styles.reverseRowContainer}>
            <button className={styles.bttn} type='submit'>
              Submit Affiliations
            </button>
          </div>
        </form>
      </div>
      <div className={` ${styles.subFormBox} `}>
        <h2 className={styles.label}>Add to Sub Fields</h2>
        {rawOutreachList.includes(outreachTypesData) && (
          <div className={`${styles.reverseRowContainer} ${styles.error}`}>
            *Outreach Type already exists
          </div>
        )}
        {rawStudyAreasList.includes(studyAreasData) && (
          <div className={`${styles.reverseRowContainer} ${styles.error}`}>
            *Study Area already exists
          </div>
        )}
        <div className={styles.reverseRowContainer}>
          <form className={styles.subForm} onSubmit={submitStudyAreasHandler}>
            <BasicFormInput
              placeholder='New area...'
              label='Add New Study Area'
              updateKey='studyArea'
              value={studyAreasData}
              onChange={handleInputChange}
            />
            <div className={styles.reverseRowContainer}>
              <button className={styles.bttn} type='submit'>
                Add Area
              </button>
            </div>
          </form>
          <form
            className={styles.subForm}
            onSubmit={submitOutreachTypesHandler}
          >
            <BasicFormInput
              placeholder='New outreach...'
              label='Add New Outreach Type'
              updateKey='outreach'
              value={outreachTypesData}
              onChange={handleInputChange}
            />
            <div className={styles.reverseRowContainer}>
              <button className={styles.bttn} type='submit'>
                Add Type
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
